import { AxiosResponse } from "axios";
import { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Notification } from "../../../../@types/Notification";
import { IBillablePatient } from "../../../../@types/Patient";
import { Button } from "../../../../components/Button";
import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { reportService } from "../../../../services/reportService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { getPortalDateFormat } from "../../../../utils/portalDateFormat";
import {
  ButtonContainer,
  CancelButton,
  ContentContainer,
  ErrorMessage,
  ModalContainer,
  ModalTitle,
  Row,
  StyledParagraph,
} from "./ReportExportModal.styles";

interface IBillingReportRequest {
  patientId?: number;
  startDate?: Dayjs;
  endDate?: Dayjs;
}

type ReportExportModalProps = {
  patient?: IBillablePatient;
  startDate?: Dayjs;
  endDate?: Dayjs;
  isBillingReport?: boolean;
  onClose: (notification?: Notification) => void;
};

export function ReportExportModal({
  patient,
  startDate,
  endDate,
  isBillingReport = false,
  onClose,
}: ReportExportModalProps) {
  const { t } = useTranslation();

  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  // EN-5272: the same day/month order as every other date field in the portal.
  const dateFormat = getPortalDateFormat();

  const handleDownloadFromServerResponse = (
    result: AxiosResponse<Blob, any>
  ) => {
    const contentDisposition = result.headers["content-disposition"];

    const fileName =
      contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
      contentDisposition?.match(/filename=(.+);?/)?.[1] ||
      "Report.xlsx";

    const url = URL.createObjectURL(new Blob([result.data]));
    const link = Object.assign(document.createElement("a"), {
      href: url,
      download: fileName,
    });

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleOnSubmitForm = async ({
    patientId,
    startDate,
    endDate,
  }: IBillingReportRequest) => {
    if (startDate === undefined || endDate === undefined) return;

    setIsDownloadingReport(true);

    const notification: Notification = {
      show: true,
      message: "",
      type: "success",
      width: "max-content",
    };

    if (isBillingReport) {
      mixpanelActions.track("User Action: Export billing report");
      const result = await reportService.downloadBillingReport(
        startDate.toISOString(),
        endDate.toISOString()
      );

      if (result.status >= 200 && result.status < 300) {
        handleDownloadFromServerResponse(result);

        notification.message = t("billing_exportModal_downloaded");
      } else {
        notification.type = "error";
        notification.message = t("billing_exportModal_billingFailed");
      }
    } else {
      mixpanelActions.track("User Action: Export patient report");
      if (patientId === undefined) return;

      const result = await reportService.downloadPatientReport(
        patientId,
        startDate.format("YYYY-MM-DD"),
        endDate.format("YYYY-MM-DD")
      );

      if (result.status >= 200 && result.status < 300) {
        handleDownloadFromServerResponse(result);

        notification.message = t("billing_exportModal_downloaded");
      } else {
        notification.type = "error";
        notification.message = t("billing_exportModal_patientFailed");
      }
    }

    setIsDownloadingReport(false);
    onClose(notification);
  };

  const formValidationSchema = yup.object().shape({
    patientId: yup.number(),
    startDate: yup
      .date()
      .required(t("billing_exportModal_startDateRequired"))
      .typeError(t("billing_exportModal_startDateInvalid")),
    endDate: yup
      .date()
      .required(t("billing_exportModal_endDateRequired"))
      .min(yup.ref("startDate"), t("billing_exportModal_endDateAfterStart")),
  });

  const formik = useFormik<IBillingReportRequest>({
    initialValues: {
      patientId: patient?.pmpId,
      startDate,
      endDate,
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const shouldShowErrorMessage = (field: "startDate" | "endDate") => {
    return formik.touched[field] ? formik.errors[field] : "";
  };

  return (
    <ModalContainer>
      <ModalTitle>
        {isBillingReport
          ? t("billing_exportModal_titleBilling")
          : t("billing_exportModal_titlePatient")}
      </ModalTitle>
      <ContentContainer>
        <Row>
          <p>{t("billing_exportModal_format")}</p>
          <StyledParagraph>{t("billing_exportModal_excel")}</StyledParagraph>
        </Row>
        {patient !== undefined && !isBillingReport && (
          <Row>
            <p>{t("billing_exportModal_patient")}</p>
            <StyledParagraph>{patient.name}</StyledParagraph>
          </Row>
        )}
        <Row dateField>
          <p>{t("billing_exportModal_startDate")}</p>
          <DateTimePickerInput
            showTime={false}
            format={dateFormat}
            placeholder={dateFormat}
            width="164px"
            defaultValue={formik.values.startDate}
            onChange={(value: Dayjs) =>
              formik.setFieldValue("startDate", value)
            }
          />
          <ErrorMessage>{shouldShowErrorMessage("startDate")}</ErrorMessage>
        </Row>
        <Row dateField>
          <p>{t("billing_exportModal_endDate")}</p>
          <DateTimePickerInput
            showTime={false}
            format={dateFormat}
            placeholder={dateFormat}
            width="164px"
            defaultValue={formik.values.endDate}
            // Clearing hands back null despite the signature, hence the guard.
            onChange={(value: Dayjs | null) =>
              formik.setFieldValue("endDate", value?.endOf("day"))
            }
          />
          <ErrorMessage>{shouldShowErrorMessage("endDate")}</ErrorMessage>
        </Row>
      </ContentContainer>
      <ButtonContainer>
        <Button
          onClick={formik.submitForm}
          type="submit"
          label={t("billing_exportModal_download")}
          isLoading={isDownloadingReport}
        />
        <CancelButton onClick={() => onClose()}>
          {t("common_cancel")}
        </CancelButton>
      </ButtonContainer>
    </ModalContainer>
  );
}
